import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Combobox, type Source } from '@/components/Combobox'
import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, DetailSection, detailActionClass, detailDangerClass } from '@/components/data/DetailPane'
import { SplitView } from '@/components/data/SplitView'
import { useSelection } from '@/lib/useSelection'

import { SectionStrip } from './SectionLabel'
import type { DialogState } from './types'

type Scheme = { name: string } | null | undefined

type SchemeRow = {
  id: string
  label: string
  current: Scheme
  /** picker for a new scheme; absent — Jira Cloud has no per-project assign endpoint */
  picker?: { source: Source; valueKey: string; apply: (schemeId: string) => DialogState | null }
  /** operation page that manages this scheme instead */
  manageOp?: string
}

const current = (r: SchemeRow) => (r.current === undefined ? 'loading…' : r.current?.name ?? '—')

const COLUMNS: Column<SchemeRow>[] = [
  { key: 'label', header: 'Scheme', width: '30%' },
  { key: 'current', header: 'Current', mono: true, render: current, title: current },
]

function SchemeDetail({ row, onDialog }: { row: SchemeRow; onDialog: (d: DialogState) => void }) {
  const [val, setVal] = useState('')
  return (
    <DetailPane
      eyebrow={row.label}
      title={<span className="font-mono">{current(row)}</span>}
      actions={row.manageOp && (
        <Link to={`/operations/${row.manageOp}`} className={detailActionClass}>
          Manage in Operations <ArrowRight aria-hidden className="size-3.5" />
        </Link>
      )}
    >
      {row.picker
        ? (
          <DetailSection title={`Change ${row.label.toLowerCase()}`}>
            <Combobox
              source={row.picker.source}
              valueKey={row.picker.valueKey}
              deps={{}}
              multi={false}
              value={val}
              onChange={setVal}
              ariaLabel={`New ${row.label.toLowerCase()}`}
              className=""
              onSelect={(p) => {
                const d = p ? row.picker!.apply(p) : null
                if (d) onDialog(d)
              }}
            />
            <p className="mt-1.5 text-[12px] text-text-3">Pick a scheme — the change is previewed before it applies.</p>
          </DetailSection>
        )
        : <p className="text-text-2">Jira Cloud has no per-project assign endpoint for this scheme.</p>}
    </DetailPane>
  )
}

export function SchemesSection({
  projectKey, projectId, permScheme, notifScheme, secScheme, priScheme, refresh, onDialog,
}: {
  projectKey: string
  projectId: string | null
  permScheme: Scheme
  notifScheme: Scheme
  secScheme: Scheme
  priScheme: Scheme
  refresh: () => void
  onDialog: (d: DialogState) => void
}) {
  const [sel, setSel] = useSelection()
  // every apply() passes `onDone: refresh` — otherwise the shown scheme stays stale after a change
  const rows: SchemeRow[] = [
    {
      id: 'permission', label: 'Permission scheme', current: permScheme,
      picker: {
        source: 'permission_scheme', valueKey: 'id',
        apply: (id) => ({
          op: 'projects.assign_permission_scheme',
          params: { project_key: projectKey, scheme_id: Number(id) },
          title: 'Change permission scheme',
          onDone: refresh,
        }),
      },
    },
    { id: 'notification', label: 'Notification scheme', current: notifScheme, manageOp: 'projects.update_notification_scheme' },
    {
      id: 'issue-type', label: 'Issue-type scheme', current: null,
      picker: {
        source: 'issue_type_scheme', valueKey: 'id',
        apply: (id) => !projectId ? null : ({
          op: 'projects.assign_issue_type_scheme',
          params: { scheme_id: id, project_id: projectId },
          title: 'Change issue-type scheme',
          onDone: refresh,
        }),
      },
    },
    {
      id: 'security', label: 'Security scheme', current: secScheme,
      picker: {
        source: 'security_scheme', valueKey: 'id',
        apply: (id) => !projectId ? null : ({
          op: 'projects.assign_issue_security_scheme',
          params: { project_id: projectId, scheme_id: id },
          title: 'Change security scheme',
          onDone: refresh,
        }),
      },
    },
    {
      id: 'priority', label: 'Priority scheme', current: priScheme,
      picker: {
        source: 'priority_scheme', valueKey: 'id',
        apply: (id) => !projectId ? null : ({
          op: 'workflows.update_priority_scheme',
          params: { scheme_id: id, project_ids_add: [projectId] },
          title: 'Change priority scheme',
          onDone: refresh,
        }),
      },
    },
  ]
  const selected = rows.find((r) => r.id === sel) ?? null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionStrip label="schemes" onRefresh={refresh} />
      <SplitView
        onCloseDetail={() => setSel(null)}
        detail={selected && <SchemeDetail key={selected.id} row={selected} onDialog={onDialog} />}
        list={<DataTable label="Schemes" rows={rows} columns={COLUMNS} rowId={(r) => r.id} selectedId={sel} onSelect={setSel} />}
      />
    </div>
  )
}

/** Project lifecycle actions for the page header: destructive ones are red outlines, never the accent. */
export function AdminActions({ projectKey, projectName, onDialog }: {
  projectKey: string
  projectName: string
  onDialog: (d: DialogState) => void
}) {
  return (
    <>
      <button
        type="button"
        className={detailActionClass}
        onClick={() => onDialog({
          op: 'projects.restore_project',
          params: { project_key: projectKey },
          title: `Restore ${projectName}`,
        })}
      >
        Restore project
      </button>
      <button
        type="button"
        className={detailDangerClass}
        onClick={() => onDialog({
          op: 'projects.archive_project',
          params: { project_key: projectKey },
          title: `Archive ${projectName}`,
          critical: true,
        })}
      >
        Archive project
      </button>
      <button
        type="button"
        className={detailDangerClass}
        onClick={() => onDialog({
          op: 'projects.delete_project',
          params: { project_key: projectKey },
          title: `Delete ${projectName}`,
          critical: true,
        })}
      >
        Delete project
      </button>
    </>
  )
}

